/**
 * Grafico da metrica aberta, com periodo escolhivel e inspecao por arraste.
 *
 * Responsabilidade: desenhar a serie que o `store` guarda em `serie` para o
 * periodo escolhido (`estado.periodoGrafico`), deixar trocar de periodo,
 * deixar arrastar para ler ponto a ponto, e — so no VPD — sobrepor a faixa
 * ideal do estagio e dizer, em texto, o que fazer quando a leitura esta fora
 * dela.
 *
 * NAO e responsabilidade deste arquivo: buscar a serie (`acoes.carregarHistorico`,
 * disparado por `acompanharGrafico()`), nem decidir a faixa ideal. Os limites
 * vem prontos do aparelho em `vpd_lo`/`vpd_hi` (§5), calculados pelo firmware a
 * partir do `plant_stage`. Recalcula-los aqui criaria uma segunda tabela de
 * estagios que divergiria da do firmware na primeira mudanca — o mesmo motivo
 * pelo qual a PWA local tambem so apresenta.
 *
 * ## Por que a serie e reduzida antes de virar linha
 *
 * 30 dias a um balde por hora ja chegam agregados do servidor (`acoes.ts`),
 * mas mesmo assim podem passar de mil pontos — muito mais do que a largura do
 * grafico em pixels, e um `<polyline>` com essa quantidade de vertices
 * corrompe a rasterizacao em algumas GPUs moveis (faixas horizontais
 * coloridas, visto no painel local). A reducao AQUI guarda o MINIMO e o
 * MAXIMO de cada balde, e nao a media, pelo mesmo motivo que `acoes.ts` faz o
 * mesmo no servidor: achatar um pico e exatamente perder a informacao pela
 * qual alguem abre o grafico.
 *
 * O rotulo de minimo–maximo, porem, sai sempre da serie COMPLETA. Se saisse da
 * reduzida, o resumo poderia mentir sobre o pico real do periodo.
 *
 * ## Por que o eixo X e tempo, e nao indice
 *
 * Um aparelho offline por dias no meio de uma janela de 30 d nao pode virar
 * uma lacuna invisivel: `x` vem de `(ts - t0) / (t1 - t0)`, com `t0`/`t1` a
 * JANELA PEDIDA — nao os extremos do dado —, entao um trecho sem amostra fica
 * um trecho vazio no desenho, exatamente como esta na realidade.
 */

import { acharMetrica, formatar, nomeDoEstagio, type Metrica } from "./../metricas";
import * as store from "./../store";
import type { TelemetrySample } from "./../tipos";
import { dataHora, dataHoraDeEpoch, el, num, relogio, svg, type Painel } from "./../ui";

const ALTURA = 120;
const PAD = 10;
const MAX_BALDES = 100;
/** Fallback ate o `ResizeObserver` medir a caixa pela primeira vez. */
const LARGURA_INICIAL = 328;

/** Acima disto o balao mostra data e hora, e nao so `HH:MM` — uma janela de
 * 30 dias nao se identifica so pela hora do dia. */
const HORAS_SO_RELOGIO = 30;

interface Ponto {
  x: number;
  y: number;
}

interface Opcao {
  valor: string;
  rotulo: string;
}

/** Fixas, e "personalizado…" para o resto. 30 d e o teto do plano `free`
 * (`api/app/services/tiers.py`); quem tem mais alcance usa o personalizado. */
const PERIODOS: Opcao[] = [
  { valor: "6", rotulo: "6 h" },
  { valor: "24", rotulo: "24 h" },
  { valor: "168", rotulo: "7 dias" },
  { valor: "720", rotulo: "30 dias" },
  { valor: "custom", rotulo: "personalizado…" },
];

export function montar(): Painel {
  let largura = LARGURA_INICIAL;
  let plotadas: TelemetrySample[] = [];
  let pontos: Ponto[] = [];
  let metricaAtual: Metrica | null = null;
  let janelaAtual = { t0: 0, t1: 0 };

  const faixa = svg("rect", { class: "g-faixa", x: PAD, y: 0, width: 0, height: 0 });
  const linha = svg("polyline", { class: "g-linha", points: "" });
  const cruz = svg("line", { class: "g-cruz", x1: 0, y1: PAD, x2: 0, y2: ALTURA - PAD });
  const ponto = svg("circle", { class: "g-ponto", r: 3.5, cx: 0, cy: 0 });
  // `style.display` e nao o atributo `hidden`: em SVG o `hidden` empata com a
  // folha de estilo do proprio agente em alguns navegadores moveis, e o
  // elemento continua visivel. Mesmo cuidado do painel local.
  esconder(faixa, cruz, ponto);

  const desenho = svg("svg", {
    class: "g-svg",
    width: "100%",
    height: ALTURA,
    viewBox: `0 0 ${largura} ${ALTURA}`,
    preserveAspectRatio: "none",
  });
  desenho.append(faixa, linha, cruz, ponto);

  const balaoValor = el("span", { class: "g-balao-valor" });
  const balaoHora = el("span", { class: "g-balao-hora" });
  const balao = el("div", { class: "g-balao", hidden: true }, balaoValor, balaoHora);

  const caixa = el("div", { class: "g-caixa" }, desenho, balao);
  const vazio = el("p", { class: "hint g-vazio", hidden: true });

  const extremos = el("span");
  const rotuloEsquerda = el("span");
  const rotuloDireita = el("span");
  const regua = el("div", { class: "g-regua" }, rotuloEsquerda, extremos, rotuloDireita);

  // --- seletor de periodo -------------------------------------------------
  //
  // Mesma mecanica de `views/timelapse.ts`: um `<select>` para as opcoes
  // fixas, "personalizado…" revela dois `datetime-local`. O seletor NUNCA e
  // desabilitado, nem com a serie vazia — e o unico caminho de volta.
  //
  // Ao lado do NOME da metrica, e nao numa linha propria: o conteudo dele e
  // so "24 h" — um `<select>` esticado a largura inteira do cartao por 4
  // caracteres parecia quebrado. O valor escolhido JA aparece no proprio
  // seletor, entao o titulo nao repete o periodo mais (era "Temperatura ·
  // 24 h"; virou so "Temperatura").

  const periodoSelect = el("select", { class: "g-periodo", "aria-label": "Período" });
  for (const opcao of PERIODOS) {
    periodoSelect.append(el("option", { value: opcao.valor, text: opcao.rotulo }));
  }
  periodoSelect.value = "24";

  const de = el("input", { type: "datetime-local", "aria-label": "De" });
  const ate = el("input", { type: "datetime-local", "aria-label": "Até" });
  const personalizado = el(
    "div",
    { class: "g-custom", hidden: true },
    de,
    el("span", { class: "hint", text: "até" }),
    ate,
  );

  const conselho = el("p", { class: "g-conselho", hidden: true });
  const titulo = el("span", { class: "panel-title" });
  const cabecalhoEsquerda = el("div", { class: "panel-head-left" }, titulo, periodoSelect);
  const faixaRotulo = el("span", { class: "g-faixa-rotulo" });

  const raiz = el(
    "section",
    { class: "panel card", id: "grafico", hidden: true },
    el("div", { class: "panel-head" }, cabecalhoEsquerda, faixaRotulo),
    personalizado,
    caixa,
    vazio,
    regua,
    conselho,
  );

  // A caixa muda de largura entre celular e desktop, e o viewBox acompanha em
  // vez de esticar: assim o ponto continua redondo e o traco com a mesma
  // espessura em qualquer tamanho.
  if (typeof ResizeObserver !== "undefined") {
    new ResizeObserver((entradas) => {
      const nova = Math.round(entradas[0]?.contentRect.width ?? 0);
      if (nova <= 0 || nova === largura) return;
      largura = nova;
      desenho.setAttribute("viewBox", `0 0 ${largura} ${ALTURA}`);
      cruz.setAttribute("y1", String(PAD));
      cruz.setAttribute("y2", String(ALTURA - PAD));
      atualizar();
    }).observe(caixa);
  }

  // --- periodo -------------------------------------------------------------

  periodoSelect.addEventListener("change", () => {
    personalizado.hidden = periodoSelect.value !== "custom";
    if (periodoSelect.value === "custom") {
      if (de.value !== "" && ate.value !== "") mandarDatas();
      return;
    }
    store.definirPeriodoGrafico({ modo: "relativo", horas: Number(periodoSelect.value) });
  });

  const mandarDatas = (): void => {
    // Espera as DUAS pontas: ao contrario do timelapse, o eixo X precisa de
    // uma janela fechada dos dois lados para saber onde comeca e onde acaba.
    if (de.value === "" || ate.value === "") return;
    store.definirPeriodoGrafico({ modo: "datas", de: de.value, ate: ate.value });
  };
  de.addEventListener("change", mandarDatas);
  ate.addEventListener("change", mandarDatas);

  function sincronizarControles(periodo: store.PeriodoGrafico): void {
    if (periodo.modo === "relativo") {
      const valor = String(periodo.horas);
      if (periodoSelect.value !== valor) periodoSelect.value = valor;
      personalizado.hidden = true;
      return;
    }
    if (periodoSelect.value !== "custom") periodoSelect.value = "custom";
    personalizado.hidden = false;
    if (de.value !== periodo.de) de.value = periodo.de;
    if (ate.value !== periodo.ate) ate.value = periodo.ate;
  }

  // --- desenho ---------------------------------------------------------

  function atualizar(): void {
    const estado = store.obter();
    const alvo = store.aparelhoSelecionado();
    const metrica = acharMetrica(estado.metricaAberta);
    metricaAtual = metrica;

    if (metrica === null || alvo === null) {
      raiz.hidden = true;
      esconderBalao();
      return;
    }
    raiz.hidden = false;
    sincronizarControles(estado.periodoGrafico);
    titulo.textContent = metrica.rotulo;

    const chaveEsperada = store.chaveSerie(alvo.device.id, estado.periodoGrafico);
    const serieAtual =
      estado.serie !== null && estado.serie.chave === chaveEsperada ? estado.serie : null;
    const validas =
      serieAtual === null
        ? []
        : serieAtual.amostras.filter((a) => Number.isFinite(metrica.de(a) ?? Number.NaN));

    janelaAtual = janelaDoPeriodo(estado.periodoGrafico);
    const { esquerda, direita } = rotulosRegua(estado.periodoGrafico);
    rotuloEsquerda.textContent = esquerda;
    rotuloDireita.textContent = direita;

    const sensor = alvo.state?.sensor;
    const limites =
      metrica.chave === "vpd" && sensor?.vpd_lo !== undefined && sensor.vpd_hi !== undefined
        ? { lo: sensor.vpd_lo, hi: sensor.vpd_hi }
        : null;

    desenharFaixaRotulo(limites, alvo.state?.plant_stage);
    desenharConselho(metrica, sensor, limites);

    if (validas.length < 2) {
      linha.setAttribute("points", "");
      plotadas = [];
      pontos = [];
      esconder(faixa, cruz, ponto);
      esconderBalao();
      caixa.hidden = true;
      regua.hidden = true;
      vazio.hidden = false;
      vazio.textContent =
        serieAtual === null
          ? "Carregando o período…"
          : "Sem dado suficiente no período.";
      return;
    }

    caixa.hidden = false;
    regua.hidden = false;
    vazio.hidden = true;

    plotadas = reduzir(validas, metrica);
    const valores = plotadas.map((a) => metrica.de(a) ?? 0);

    // A escala do VPD precisa caber o dado E a faixa ideal. Sem isto, quando a
    // leitura fica inteiramente fora da faixa, os dois limites colapsam na
    // mesma borda e a faixa some — justamente quando ela mais importa.
    const dominio =
      limites === null
        ? { min: Math.min(...valores), max: Math.max(...valores) }
        : {
            min: Math.min(...valores, limites.lo),
            max: Math.max(...valores, limites.hi),
          };

    const larguraUtil = largura - 2 * PAD;
    const duracao = janelaAtual.t1 - janelaAtual.t0 || 1;
    pontos = plotadas.map((a, i) => ({
      x: Math.max(
        PAD,
        Math.min(largura - PAD, PAD + ((a.ts - janelaAtual.t0) / duracao) * larguraUtil),
      ),
      y: aY(valores[i] ?? 0, dominio),
    }));
    linha.setAttribute("points", pontos.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" "));

    // Da serie completa, nunca da reduzida.
    const todos = validas.map((a) => metrica.de(a) ?? 0);
    extremos.textContent = `${formatar(metrica, Math.min(...todos))}–${formatar(metrica, Math.max(...todos))} ${metrica.unidade}`;

    if (limites === null) {
      esconder(faixa);
    } else {
      const yHi = aY(limites.hi, dominio);
      const yLo = aY(limites.lo, dominio);
      faixa.setAttribute("x", String(PAD));
      faixa.setAttribute("width", String(Math.max(0, largura - 2 * PAD)));
      faixa.setAttribute("y", yHi.toFixed(1));
      faixa.setAttribute("height", Math.max(0, yLo - yHi).toFixed(1));
      mostrar(faixa);
    }

    // Troca de metrica, de periodo, ou quadro novo com o dedo ainda na tela de
    // um momento atras: o balao apontaria para um indice de outra serie.
    esconderBalao();
  }

  function desenharFaixaRotulo(
    limites: { lo: number; hi: number } | null,
    estagio: string | undefined,
  ): void {
    if (limites === null) {
      faixaRotulo.hidden = true;
      return;
    }
    const nome = nomeDoEstagio(estagio);
    faixaRotulo.hidden = false;
    faixaRotulo.textContent =
      `ideal ${num(limites.lo, 2)}–${num(limites.hi, 2)} kPa` +
      (nome === null ? "" : ` · ${nome}`);
  }

  function desenharConselho(
    metrica: Metrica,
    sensor: { vpd_kpa?: number | null; vpd_status?: string } | undefined,
    limites: { lo: number; hi: number } | null,
  ): void {
    const status = sensor?.vpd_status;
    if (metrica.chave !== "vpd" || limites === null || status === undefined || status === "ok") {
      conselho.hidden = true;
      conselho.textContent = "";
      conselho.className = "g-conselho";
      return;
    }
    const atual = num(sensor?.vpd_kpa ?? null, 2);
    conselho.hidden = false;
    conselho.className = "g-conselho ativo";
    conselho.textContent =
      status === "high"
        ? `VPD alto: ${atual} > ${num(limites.hi, 2)} kPa. Aumente a umidade (ligue o umidificador) ou reduza a temperatura.`
        : status === "low"
          ? `VPD baixo: ${atual} < ${num(limites.lo, 2)} kPa. Reduza a umidade (ligue o exaustor / desligue o umidificador) ou aumente a temperatura.`
          : "VPD indisponível — sem leitura do sensor.";
  }

  // --- inspecao por arraste --------------------------------------------

  function mostrarEm(clienteX: number): void {
    const total = plotadas.length;
    if (total < 2 || pontos.length !== total || metricaAtual === null) return;

    const caixaDom = desenho.getBoundingClientRect();
    if (caixaDom.width === 0) return;
    const x = ((clienteX - caixaDom.left) / caixaDom.width) * largura;

    // O eixo X e tempo e nao indice: os pontos nao ficam espacados
    // uniformemente quando ha lacuna no dado, entao o ponto mais perto de `x`
    // e achado por distancia, e nao por uma regra de tres.
    let indice = 0;
    let menorDistancia = Infinity;
    for (let i = 0; i < pontos.length; i++) {
      const candidato = pontos[i];
      if (candidato === undefined) continue;
      const distancia = Math.abs(candidato.x - x);
      if (distancia < menorDistancia) {
        menorDistancia = distancia;
        indice = i;
      }
    }

    const p = pontos[indice];
    const amostra = plotadas[indice];
    if (p === undefined || amostra === undefined) return;

    cruz.setAttribute("x1", p.x.toFixed(1));
    cruz.setAttribute("x2", p.x.toFixed(1));
    ponto.setAttribute("cx", p.x.toFixed(1));
    ponto.setAttribute("cy", p.y.toFixed(1));
    mostrar(cruz, ponto);

    balaoValor.textContent = `${formatar(metricaAtual, metricaAtual.de(amostra))} ${metricaAtual.unidade}`;
    balaoHora.textContent =
      janelaAtual.t1 - janelaAtual.t0 <= HORAS_SO_RELOGIO * 3600
        ? relogio(amostra.ts * 1000)
        : dataHoraDeEpoch(amostra.ts);

    // O balao e HTML em pixels reais, e nao um `<text>` em unidades do
    // viewBox: assim a fonte nao estica junto com o grafico.
    const proporcao = caixaDom.width / largura;
    const meio = balao.offsetWidth / 2 || 30;
    const esquerda = Math.max(
      meio,
      Math.min(caixaDom.width - meio, p.x * proporcao),
    );
    balao.style.left = `${esquerda}px`;
    balao.hidden = false;
  }

  function esconderBalao(): void {
    esconder(cruz, ponto);
    balao.hidden = true;
  }

  let arrastando = false;
  let ultimoX = 0;
  let quadroPendente = false;

  desenho.addEventListener("pointerdown", (ev) => {
    if (plotadas.length < 2) return;
    arrastando = true;
    desenho.setPointerCapture(ev.pointerId);
    mostrarEm(ev.clientX);
    ev.preventDefault();
  });

  desenho.addEventListener("pointermove", (ev) => {
    if (!arrastando) return;
    // `pointermove` chega mais rapido que o refresh da tela; repintar a cada
    // evento e o que corrompe a rasterizacao em GPU movel. Um repinte por
    // quadro, no maximo.
    ultimoX = ev.clientX;
    if (quadroPendente) return;
    quadroPendente = true;
    requestAnimationFrame(() => {
      quadroPendente = false;
      if (arrastando) mostrarEm(ultimoX);
    });
  });

  for (const evento of ["pointerup", "pointercancel", "pointerleave"]) {
    desenho.addEventListener(evento, () => {
      arrastando = false;
      esconderBalao();
    });
  }

  return { raiz, atualizar };
}

// --- ajudantes puros ----------------------------------------------------

/** As duas pontas da régua: rótulo da esquerda (início) e da direita (fim/"agora"). */
function rotulosRegua(periodo: store.PeriodoGrafico): { esquerda: string; direita: string } {
  if (periodo.modo === "relativo") {
    const opcao = PERIODOS.find((o) => Number(o.valor) === periodo.horas);
    return { esquerda: `-${opcao?.rotulo ?? `${periodo.horas} h`}`, direita: "agora" };
  }
  return { esquerda: dataHora(periodo.de), direita: dataHora(periodo.ate) };
}

/** A janela pedida, em epoch-segundos — NUNCA os extremos do dado. */
function janelaDoPeriodo(periodo: store.PeriodoGrafico): { t0: number; t1: number } {
  if (periodo.modo === "relativo") {
    const t1 = Date.now() / 1000;
    return { t0: t1 - periodo.horas * 3600, t1 };
  }
  return { t0: Date.parse(periodo.de) / 1000, t1: Date.parse(periodo.ate) / 1000 };
}

const aY = (v: number, dominio: { min: number; max: number }): number => {
  const amplitude = dominio.max - dominio.min || 1;
  return ALTURA - PAD - ((v - dominio.min) / amplitude) * (ALTURA - 2 * PAD);
};

const mostrar = (...nos: SVGElement[]): void => {
  for (const no of nos) no.style.display = "";
};

const esconder = (...nos: SVGElement[]): void => {
  for (const no of nos) no.style.display = "none";
};

/**
 * Reduz a serie a no maximo `MAX_BALDES` baldes, guardando o minimo e o maximo
 * de cada um — e devolvendo os dois em ordem cronologica, senao a linha
 * "volta no tempo" dentro do balde.
 */
function reduzir(amostras: TelemetrySample[], metrica: Metrica): TelemetrySample[] {
  const total = amostras.length;
  if (total <= MAX_BALDES * 2) return amostras;

  const tamanho = total / MAX_BALDES;
  const saida: TelemetrySample[] = [];
  for (let i = 0; i < MAX_BALDES; i++) {
    const comeco = Math.floor(i * tamanho);
    const fim = Math.min(total, Math.max(comeco + 1, Math.floor((i + 1) * tamanho)));
    let menor = amostras[comeco];
    let maior = amostras[comeco];
    if (menor === undefined || maior === undefined) continue;
    for (let j = comeco + 1; j < fim; j++) {
      const atual = amostras[j];
      if (atual === undefined) continue;
      if ((metrica.de(atual) ?? 0) < (metrica.de(menor) ?? 0)) menor = atual;
      if ((metrica.de(atual) ?? 0) > (metrica.de(maior) ?? 0)) maior = atual;
    }
    const [primeiro, segundo] = menor.ts <= maior.ts ? [menor, maior] : [maior, menor];
    saida.push(primeiro);
    if (segundo !== primeiro) saida.push(segundo);
  }
  return saida;
}
